// src/quality-management/components/project-form/projectForm.ts
// Estado e helpers compartilhados entre criação e edição de projeto

import { Project, CreateProjectRequest, ProjectStatus } from '../../services/api/types';

export interface ProjectFormState {
  name: string;
  description: string;
  selectedEmployees: number[];
  projectNumber: string;
  client: string;
  hours: string;
  value: string;
  pmId: string;
  status: ProjectStatus;
  margin: string;
  ppi: string;
  startDate: string;
  remainingTime: string;
  technicalProgress: string;
  financialProgress: string;
  endDate: string;
}

export const EMPTY_PROJECT_FORM: ProjectFormState = {
  name: '',
  description: '',
  selectedEmployees: [],
  projectNumber: '',
  client: '',
  hours: '',
  value: '',
  pmId: '',
  status: 'active',
  margin: '',
  ppi: '',
  startDate: '',
  remainingTime: '',
  technicalProgress: '',
  financialProgress: '',
  endDate: '',
};

export const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'active', label: 'Em execução' },
  { value: 'on_hold', label: 'Em espera' },
  { value: 'completed', label: 'Finalizado' },
  { value: 'cancelled', label: 'Cancelado' },
];

// Converte string de input numérico em número ou null
const toNum = (v: string): number | null => (v.trim() === '' ? null : Number(v));

// Converte valor numérico (ou null) em string para o input
const numStr = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

// ISO (UTC) -> valor de <input type="datetime-local"> (YYYY-MM-DDTHH:mm) no fuso local
export const toDatetimeLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

// Monta o payload da API a partir do estado do formulário
export function buildProjectPayload(form: ProjectFormState): CreateProjectRequest {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    assigned_employee_ids: form.selectedEmployees,
    project_number: toNum(form.projectNumber),
    client: form.client.trim() || null,
    hours: toNum(form.hours),
    value: toNum(form.value),
    pm_id: form.pmId === '' ? null : Number(form.pmId),
    status: form.status,
    margin: toNum(form.margin),
    ppi: toNum(form.ppi),
    start_date: form.startDate || null,
    remaining_time: toNum(form.remainingTime),
    technical_progress: toNum(form.technicalProgress),
    financial_progress: toNum(form.financialProgress),
    end_date: form.endDate || null,
  };
}

// Preenche o estado do formulário a partir de um projeto existente
export function projectToForm(p: Project): ProjectFormState {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    selectedEmployees: Array.isArray(p.assigned_employee_ids) ? p.assigned_employee_ids : [],
    projectNumber: numStr(p.project_number),
    client: p.client ?? '',
    hours: numStr(p.hours),
    value: numStr(p.value),
    pmId: p.pm_id ? String(p.pm_id) : '',
    status: p.status ?? 'active',
    margin: numStr(p.margin),
    ppi: numStr(p.ppi),
    startDate: toDatetimeLocal(p.start_date),
    remainingTime: numStr(p.remaining_time),
    technicalProgress: numStr(p.technical_progress),
    financialProgress: numStr(p.financial_progress),
    endDate: toDatetimeLocal(p.end_date),
  };
}
